/**
 * Resolve once every <img> intersecting the viewport has loaded (or failed),
 * or after `timeout` ms — whichever comes first. Lets the page loader lift
 * only when the hero photo and the first product cards can paint, so nothing
 * pops in after the reveal. Images inside `exclude` (the loader itself) are
 * ignored. Never rejects.
 */
export default function waitForVisibleImages({ timeout = 1500, exclude = null } = {}) {
  if (typeof document === 'undefined') return Promise.resolve();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;

  const pending = Array.from(document.images).filter((img) => {
    if (img.complete) return false;
    if (exclude && exclude.contains(img)) return false;
    const r = img.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
  });
  if (!pending.length) return Promise.resolve();

  const loads = pending.map((img) => new Promise((resolve) => {
    const done = () => {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      resolve();
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  }));

  return Promise.race([
    Promise.all(loads),
    new Promise((resolve) => setTimeout(resolve, timeout)),
  ]);
}
